/**
 * Engelse regel onder niet-Engelse Mass Message-teksten, alleen ter info (24-10-2026, Jordy).
 *
 * Eén verzoek voor alle teksten op het scherm (gebundeld per 60 ms, hooguit 100 per keer),
 * en een geheugen per tekst in deze browser. De server slaat elke vertaling op per tekst
 * (hash), dus een herhaalde weergave kost geen nieuwe vertaling. "pending" (nog niet
 * vertaald binnen het tijdbudget van de server) wordt na een paar seconden opnieuw gevraagd.
 * Een fout geeft "unavailable": de regel mag nooit verzenden of bewerken tegenhouden.
 */
import { useEffect, useState } from 'react'
import { api } from './api'

export interface EnUitkomst { status: 'english' | 'translated' | 'unavailable' | 'pending'; en: string | null }

const geheugen = new Map<string, EnUitkomst>()
const luisteraars = new Map<string, Set<(u: EnUitkomst) => void>>()
let rij = new Set<string>()
let klok: number | null = null
const pogingen = new Map<string, number>()

function sleutel(tekst: string): string {
  return tekst.replace(/\s+/g, ' ').trim()
}

function meld(k: string, u: EnUitkomst) {
  geheugen.set(k, u)
  luisteraars.get(k)?.forEach((f) => f(u))
}

async function verstuur() {
  klok = null
  const teksten = [...rij]
  rij = new Set()
  for (let i = 0; i < teksten.length; i += 100) {
    const deel = teksten.slice(i, i + 100)
    let items: EnUitkomst[] = []
    try {
      items = (await api.post<{ items: EnUitkomst[] }>('/api/admin/mass-auto/translate-en', { texts: deel })).items
    } catch {
      items = deel.map(() => ({ status: 'unavailable', en: null }))
    }
    deel.forEach((k, j) => {
      const u = items[j] ?? { status: 'unavailable', en: null }
      if (u.status === 'pending') {
        const n = (pogingen.get(k) ?? 0) + 1
        pogingen.set(k, n)
        if (n <= 6) {
          window.setTimeout(() => vraag(k), 3000)
          return
        }
        meld(k, { status: 'unavailable', en: null })
        return
      }
      meld(k, u)
    })
  }
}

function vraag(k: string) {
  rij.add(k)
  if (klok == null) klok = window.setTimeout(() => { void verstuur() }, 60)
}

/** De Engelse regel voor deze tekst (null zolang er niets te tonen of op te vragen is). */
export function useEngels(tekst: string | null | undefined): EnUitkomst | null {
  const k = sleutel(tekst ?? '')
  const [u, setU] = useState<EnUitkomst | null>(() => (k ? geheugen.get(k) ?? null : null))
  useEffect(() => {
    if (!k) { setU(null); return }
    const bekend = geheugen.get(k)
    setU(bekend ?? { status: 'pending', en: null })
    const f = (x: EnUitkomst) => setU(x)
    if (!luisteraars.has(k)) luisteraars.set(k, new Set())
    luisteraars.get(k)!.add(f)
    if (!bekend) vraag(k)
    return () => { luisteraars.get(k)?.delete(f) }
  }, [k])
  return u
}
